import React, { memo, useCallback } from 'react';
import { Handle, Position } from 'reactflow';
import { Paper, Typography, Box, alpha, Chip } from '@mui/material';
import { AccountTree } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';

const SubprocessNode = memo(({ data, selected, id }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  // Deep purple theme colors
  const primaryColor = theme.palette.secondary.main; // Deep purple
  const secondaryColor = theme.palette.secondary.dark; // Darker purple
  const accentColor = theme.palette.secondary.light; // Purple accent
  const textColor = theme.palette.common.white;

  const handleDoubleClick = useCallback((event) => {
    event.stopPropagation();
    const editEvent = new CustomEvent('editNode', { detail: { nodeId: id } });
    window.dispatchEvent(editEvent);
  }, [id]);

  const handleContextMenu = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    const contextEvent = new CustomEvent('nodeContextMenu', {
      detail: {
        nodeId: id,
        x: event.clientX,
        y: event.clientY
      }
    });
    window.dispatchEvent(contextEvent);
  }, [id]);

  // Get subprocess status color
  const getStatusColor = (status) => {
    switch(status) {
      case 'active':
        return theme.palette.success.main;
      case 'inactive':
        return theme.palette.grey[500];
      case 'error':
        return theme.palette.error.main;
      case 'pending':
        return theme.palette.warning.main;
      default:
        return primaryColor;
    }
  };

  // Format subprocess ID display
  const formatSubprocessId = (subprocessId) => {
    if (!subprocessId) return 'No ID';
    if (subprocessId.length > 8) {
      return `${subprocessId.substring(0, 8)}...`;
    }
    return subprocessId;
  };

  return (
    <Box sx={{ position: 'relative' }}>
      <Paper
        elevation={selected ? 12 : 6}
        onDoubleClick={handleDoubleClick}
        onContextMenu={handleContextMenu}
        sx={{
          padding: 2.5,
          borderRadius: 2,
          minWidth: 180,
          minHeight: 100,
          position: 'relative',
          cursor: 'move',
          background: `linear-gradient(145deg, ${primaryColor} 0%, ${secondaryColor} 100%)`,
          // Double border effect
          border: `4px solid ${alpha(accentColor, 0.3)}`,
          outline: selected
            ? `2px solid ${theme.palette.secondary.main}`
            : `2px solid ${alpha(primaryColor, 0.5)}`,
          outlineOffset: '2px',
          transition: theme.transitions.create([
            'transform', 'box-shadow', 'outline', 'filter'
          ], {
            duration: theme.transitions.duration.shorter,
          }),
          '&:hover': {
            transform: 'translateY(-4px) scale(1.03)',
            boxShadow: `0 16px 48px ${alpha(primaryColor, 0.4)}`,
            outline: `3px solid ${alpha(theme.palette.secondary.main, 0.6)}`,
            filter: 'brightness(1.1)',
            '& .subprocess-icon': {
              transform: 'scale(1.15) rotate(10deg)',
            },
            '& .inner-border': {
              borderColor: alpha(accentColor, 0.6),
            },
            '& .connection-lines': {
              opacity: 1,
            }
          },
          '&::before': {
            content: '""',
            position: 'absolute',
            top: -8,
            left: -8,
            right: -8,
            bottom: -8,
            borderRadius: 'inherit',
            background: `conic-gradient(from 0deg, ${primaryColor}, ${accentColor}, ${secondaryColor}, ${primaryColor})`,
            opacity: selected ? 0.4 : 0,
            transition: 'opacity 0.3s ease',
            zIndex: -1,
          }
        }}
        role="button"
        tabIndex={0}
        aria-label={`Subprocess node: ${data.label || 'Subprocess'}`}
        aria-selected={selected}
      >
        {/* Inner border for double border effect */}
        <Box
          className="inner-border"
          sx={{
            position: 'absolute',
            top: 4,
            left: 4,
            right: 4,
            bottom: 4,
            border: `2px dashed ${alpha(accentColor, 0.4)}`,
            borderRadius: 1,
            transition: 'border-color 0.3s ease',
          }}
        />

        {/* Connection lines decoration */}
        <Box
          className="connection-lines"
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            width: 20,
            height: 20,
            opacity: 0.6,
            transition: 'opacity 0.3s ease',
            '&::before, &::after': {
              content: '""',
              position: 'absolute',
              background: alpha(textColor, 0.4),
            },
            '&::before': {
              width: '100%',
              height: '1px',
              top: '50%',
              left: 0,
            },
            '&::after': {
              width: '1px',
              height: '100%',
              top: 0,
              left: '50%',
            }
          }}
        />

        {/* Input Handle */}
        <Handle
          type="target"
          position={Position.Top}
          style={{
            background: `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})`,
            border: `2px solid ${theme.palette.background.paper}`,
            width: 14,
            height: 14,
            boxShadow: `0 2px 8px ${alpha(primaryColor, 0.3)}`,
          }}
        />

        {/* Header with icon and title */}
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          mb: 1.5,
          zIndex: 1,
          position: 'relative',
        }}>
          <Box sx={{
            p: 0.8,
            borderRadius: 1.5,
            background: `linear-gradient(135deg, ${accentColor}, ${alpha(accentColor, 0.8)})`,
            display: 'flex',
            mr: 1.5,
            boxShadow: `0 4px 12px ${alpha(accentColor, 0.3)}`,
          }}>
            <AccountTree
              className="subprocess-icon"
              sx={{
                fontSize: 24,
                color: textColor,
                transition: 'transform 0.3s ease',
                filter: `drop-shadow(0 2px 4px ${alpha(theme.palette.common.black, 0.3)})`,
              }}
            />
          </Box>
          <Typography
            variant="subtitle1"
            fontWeight="700"
            sx={{
              color: textColor,
              fontSize: '1rem',
              textShadow: `0 1px 2px ${alpha(theme.palette.common.black, 0.3)}`,
              flex: 1,
            }}
          >
            {data.label || 'Subprocess'}
          </Typography>
        </Box>

        {/* Subprocess name/description */}
        {data.subprocessName && (
          <Typography
            variant="body2"
            sx={{
              color: alpha(textColor, 0.9),
              mb: 1,
              fontSize: '0.85rem',
              fontWeight: 500,
              textShadow: `0 1px 2px ${alpha(theme.palette.common.black, 0.2)}`,
              maxWidth: '100%',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {data.subprocessName}
          </Typography>
        )}

        {/* Subprocess ID */}
        {data.subprocessId && (
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <Typography
              variant="caption"
              sx={{
                color: alpha(textColor, 0.7),
                mr: 1,
                fontSize: '0.7rem',
                textShadow: `0 1px 2px ${alpha(theme.palette.common.black, 0.2)}`,
              }}
            >
              ID:
            </Typography>
            <Chip
              label={formatSubprocessId(data.subprocessId)}
              size="small"
              sx={{
                height: 20,
                fontSize: '0.65rem',
                backgroundColor: alpha(theme.palette.common.white, 0.2),
                color: textColor,
                fontWeight: 600,
                border: `1px solid ${alpha(theme.palette.common.white, 0.3)}`,
                backdropFilter: 'blur(4px)',
              }}
            />
          </Box>
        )}

        {/* Status indicator */}
        {data.status && (
          <Box
            sx={{
              position: 'absolute',
              top: -8,
              right: -8,
              width: 24,
              height: 24,
              borderRadius: '50%',
              background: `linear-gradient(135deg, ${getStatusColor(data.status)}, ${alpha(getStatusColor(data.status), 0.8)})`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: theme.palette.common.white,
              fontSize: '0.7rem',
              fontWeight: 'bold',
              border: `2px solid ${theme.palette.background.paper}`,
              boxShadow: `0 2px 8px ${alpha(theme.palette.common.black, 0.2)}`,
              animation: data.status === 'active' ? 'status-pulse 2s infinite' : 'none',
              '@keyframes status-pulse': {
                '0%': {
                  boxShadow: `0 2px 8px ${alpha(getStatusColor(data.status), 0.4)}`,
                },
                '50%': {
                  boxShadow: `0 4px 16px ${alpha(getStatusColor(data.status), 0.6)}`,
                },
                '100%': {
                  boxShadow: `0 2px 8px ${alpha(getStatusColor(data.status), 0.4)}`,
                },
              }
            }}
          >
            {data.status === 'active' ? '▶' :
             data.status === 'inactive' ? '⏸' :
             data.status === 'error' ? '✕' :
             data.status === 'pending' ? '⏳' : '●'}
          </Box>
        )}

        {/* Output Handle */}
        <Handle
          type="source"
          position={Position.Bottom}
          style={{
            background: `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})`,
            border: `2px solid ${theme.palette.background.paper}`,
            width: 14,
            height: 14,
            boxShadow: `0 2px 8px ${alpha(primaryColor, 0.3)}`,
          }}
        />
      </Paper>

      {/* Status label */}
      {data.status && (
        <Chip
          label={data.status.toUpperCase()}
          size="small"
          sx={{
            position: 'absolute',
            bottom: -25,
            left: '50%',
            transform: 'translateX(-50%)',
            backgroundColor: getStatusColor(data.status),
            color: textColor,
            fontSize: '0.65rem',
            height: 20,
            fontWeight: 700,
            border: `1px solid ${alpha(theme.palette.common.white, 0.3)}`,
            backdropFilter: 'blur(4px)',
            opacity: selected ? 1 : 0.8,
            transition: theme.transitions.create('opacity'),
          }}
        />
      )}

      {/* Version indicator if available */}
      {data.version && (
        <Chip
          label={`v${data.version}`}
          size="small"
          sx={{
            position: 'absolute',
            top: -25,
            left: '50%',
            transform: 'translateX(-50%)',
            backgroundColor: alpha(accentColor, 0.9),
            color: textColor,
            fontSize: '0.65rem',
            height: 20,
            fontWeight: 600,
            border: `1px solid ${alpha(theme.palette.common.white, 0.3)}`,
            backdropFilter: 'blur(4px)',
          }}
        />
      )}
    </Box>
  );
});

SubprocessNode.displayName = 'SubprocessNode';

export default SubprocessNode;